import { useState } from "react";
import { Check, Copy, Plus, Tag, Terminal } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { log } from "../../lib/log";
import { Card, Field } from "./_shared";

// ---------------------------------------------------------------------------
// SAS / Trumf Tab — single-merchant add + bulk scraper (collapsed)
// ---------------------------------------------------------------------------
export function ProgramTab({ onChange, program, accent, href, snippet, programName }) {
  const { apiFetch } = useAuth();
  const isSas = program === "sas";
  const empty = isSas
    ? { name: "", host: "", points_text: "", deal_text: "", nivaapoeng_bonus: false }
    : { name: "", host: "", bonus_text: "", bonus_capped: false, affiliate_url: "" };
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [importing, setImporting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.host.trim()) return;
    setSubmitting(true);
    setResult(null);
    try {
      const d = await apiFetch("/api/programs/add", {
        method: "POST",
        body: JSON.stringify({ program, ...form }),
      });
      if (d.ok) {
        setResult({ ok: true, msg: `${d.inserted ? "Added" : "Updated"}: ${d.host}` });
        setForm(empty);
        onChange?.();
      } else {
        setResult({ ok: false, msg: JSON.stringify(d) });
      }
    } catch (err) {
      setResult({ ok: false, msg: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const copySnippet = async () => {
    try { await navigator.clipboard.writeText(snippet || ""); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch (e) { log.warn("[admin] clipboard copy failed:", e?.message); }
  };
  const pasteAndImport = async () => {
    setBulkResult(null);
    let text;
    try { text = await navigator.clipboard.readText(); }
    catch { setBulkResult({ ok: false, msg: "Couldn't read clipboard. Run the snippet first." }); return; }
    let merchants;
    try {
      merchants = JSON.parse(text);
      if (!Array.isArray(merchants)) throw new Error("not an array");
    } catch { setBulkResult({ ok: false, msg: "Clipboard doesn't contain merchant JSON." }); return; }
    setImporting(true);
    try {
      const d = await apiFetch("/api/programs/import", {
        method: "POST",
        body: JSON.stringify({ program, merchants }),
      });
      if (d.ok) {
        setBulkResult({ ok: true, msg: `Imported ${d.matched}/${d.received} (inserted ${d.inserted}, updated ${d.updated}, unresolved ${d.unresolved})` });
        onChange?.();
      } else {
        setBulkResult({ ok: false, msg: JSON.stringify(d) });
      }
    } catch (e) {
      setBulkResult({ ok: false, msg: e.message });
    } finally { setImporting(false); }
  };

  const placeholder = isSas
    ? { name: "Komplett", host: "komplett.no", main: "Tjen 15 poeng per 100 kr", extra: "Black Friday: dobbel poeng" }
    : { name: "Meny", host: "meny.no", main: "3%", extra: "https://trumfnetthandel.no/butikk/meny" };

  return (
    <div className="space-y-px">
      <Card title={`ADD A ${isSas ? "SAS EUROBONUS" : "TRUMF NETTHANDEL"} MERCHANT`}
            subtitle={`Tag a site as part of the ${programName} program.`} accent={accent} testid={`${program}-add-card`}>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
          <Field label="Merchant name" testid={`${program}-name`}>
            <input
              required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={placeholder.name}
              data-testid={`${program}-name-input`}
              className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] outline-none px-4 py-3 text-sm text-[var(--srs-ink)] placeholder:text-[var(--srs-ink-faint)] transition-colors focus:border-[var(--accent)]"
              style={{ ['--accent']: accent }}
            />
          </Field>
          <Field label="Website host" testid={`${program}-host`}>
            <input
              required value={form.host}
              onChange={(e) => setForm({ ...form, host: e.target.value })}
              placeholder={placeholder.host}
              data-testid={`${program}-host-input`}
              className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] outline-none px-4 py-3 text-sm text-[var(--srs-ink)] placeholder:text-[var(--srs-ink-faint)] font-mono transition-colors focus:border-[var(--accent)]"
              style={{ ['--accent']: accent }}
            />
          </Field>

          {isSas ? (
            <>
              <Field label="Points text" testid="sas-points">
                <input
                  required value={form.points_text}
                  onChange={(e) => setForm({ ...form, points_text: e.target.value })}
                  placeholder={placeholder.main}
                  data-testid="sas-points-input"
                  className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] outline-none px-4 py-3 text-sm text-[var(--srs-ink)] placeholder:text-[var(--srs-ink-faint)] transition-colors focus:border-[#6A9CFF]"
                />
              </Field>
              <Field label="Deal (optional)" testid="sas-deal">
                <input
                  value={form.deal_text}
                  onChange={(e) => setForm({ ...form, deal_text: e.target.value })}
                  placeholder={placeholder.extra}
                  data-testid="sas-deal-input"
                  className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] outline-none px-4 py-3 text-sm text-[var(--srs-ink)] placeholder:text-[var(--srs-ink-faint)] transition-colors focus:border-[#6A9CFF]"
                />
              </Field>
              <label className="flex items-center gap-3 sm:col-span-2 cursor-pointer select-none" data-testid="sas-nivaa">
                <input type="checkbox" checked={form.nivaapoeng_bonus}
                  onChange={(e) => setForm({ ...form, nivaapoeng_bonus: e.target.checked })}
                  data-testid="sas-nivaa-input"
                  className="w-4 h-4 accent-[#6A9CFF]" />
                <span className="text-xs text-[var(--srs-ink)]">Includes <strong className="text-[var(--srs-ink)]">Nivåpoeng</strong> bonus</span>
              </label>
            </>
          ) : (
            <>
              <Field label="Bonus text" testid="trumf-bonus">
                <input
                  required value={form.bonus_text}
                  onChange={(e) => setForm({ ...form, bonus_text: e.target.value })}
                  placeholder={placeholder.main}
                  data-testid="trumf-bonus-input"
                  className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] outline-none px-4 py-3 text-sm text-[var(--srs-ink)] placeholder:text-[var(--srs-ink-faint)] transition-colors focus:border-[#C4262E]"
                />
              </Field>
              <Field label="Affiliate URL (optional)" testid="trumf-url">
                <input
                  value={form.affiliate_url}
                  onChange={(e) => setForm({ ...form, affiliate_url: e.target.value })}
                  placeholder={placeholder.extra}
                  data-testid="trumf-url-input"
                  className="w-full bg-[var(--srs-surface-2)] border border-[var(--srs-line)] outline-none px-4 py-3 text-sm text-[var(--srs-ink)] placeholder:text-[var(--srs-ink-faint)] font-mono transition-colors focus:border-[#C4262E]"
                />
              </Field>
              <label className="flex items-center gap-3 sm:col-span-2 cursor-pointer select-none" data-testid="trumf-capped">
                <input type="checkbox" checked={form.bonus_capped}
                  onChange={(e) => setForm({ ...form, bonus_capped: e.target.checked })}
                  data-testid="trumf-capped-input"
                  className="w-4 h-4 accent-[#C4262E]" />
                <span className="text-xs text-[var(--srs-ink)]">Bonus is <strong className="text-[var(--srs-ink)]">capped</strong> (display as "Opptil …")</span>
              </label>
            </>
          )}

          <div className="sm:col-span-2 flex flex-wrap items-center gap-4 pt-2">
            <button
              type="submit" disabled={submitting}
              data-testid={`${program}-submit-btn`}
              className="inline-flex items-center gap-2 px-6 py-3 disabled:bg-[var(--srs-line-soft)] disabled:text-[var(--srs-ink-faint)] text-black text-xs tracking-[0.25em] font-bold uppercase transition-colors hover:opacity-90"
              style={{ background: submitting ? "#52525B" : accent }}
            >
              <Plus className="w-4 h-4" /> {submitting ? "Adding…" : "Add to database"}
            </button>
            {result && (
              <span data-testid={`${program}-add-result`} className={`text-xs tracking-wide ${result.ok ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                {result.ok ? "✓ " : "✗ "}{result.msg}
              </span>
            )}
          </div>
        </form>
      </Card>

      <details className="border border-[var(--srs-line)] bg-[var(--srs-surface)]">
        <summary className="cursor-pointer p-5 text-[11px] tracking-[0.25em] font-bold text-[var(--srs-ink-dim)] hover:text-[var(--srs-ink)] transition-colors flex items-center gap-2">
          <Terminal className="w-3.5 h-3.5" style={{ color: accent }} /> ADVANCED · BULK SCRAPE FROM {programName.toUpperCase()}
        </summary>
        <div className="p-5 pt-0 space-y-4">
          <ol className="text-xs text-[var(--srs-ink-dim)] leading-relaxed list-decimal pl-4 space-y-1">
            <li>Open <a href={href} target="_blank" rel="noreferrer" className="text-[var(--srs-ink)] underline decoration-[#10B981] underline-offset-4 break-all">{new URL(href).host}</a> and wait for the page to load.</li>
            <li><strong className="text-[var(--srs-ink)]">F12 → Console</strong>, paste snippet, press Enter. It copies merchant JSON to your clipboard.</li>
            <li>Come back here and click <strong className="text-[var(--srs-ink)]">Paste &amp; import</strong>.</li>
          </ol>
          <div className="grid grid-cols-2 gap-2 max-w-md">
            <button onClick={copySnippet} disabled={!snippet}
              data-testid={`copy-${program}-snippet`}
              className="inline-flex items-center justify-center gap-2 px-3 py-2.5 text-[10px] tracking-[0.2em] font-bold uppercase transition-colors bg-[var(--srs-surface-2)] text-[var(--srs-ink)] disabled:opacity-50"
              style={{ border: `1px solid ${snippet ? accent : 'var(--srs-line)'}` }}>
              {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy snippet</>}
            </button>
            <button onClick={pasteAndImport} disabled={importing}
              data-testid={`paste-import-${program}`}
              className="inline-flex items-center justify-center gap-2 px-3 py-2.5 text-[10px] tracking-[0.2em] font-bold uppercase text-white transition-colors disabled:opacity-50"
              style={{ background: importing ? "var(--srs-ink-faint)" : accent }}>
              {importing ? "Importing…" : "Paste & import"}
            </button>
          </div>
          {snippet ? (
            <pre data-testid={`${program}-snippet-code`}
                 className="text-[11px] leading-relaxed text-[var(--srs-ink)] p-4 max-h-[220px] overflow-auto whitespace-pre-wrap break-all bg-[var(--srs-surface-2)] border border-[var(--srs-line)] rounded">
              {snippet}
            </pre>
          ) : (
            <div className="text-[11px] text-[var(--srs-ink-faint)]">Loading snippet…</div>
          )}
          {bulkResult && (
            <div className={`text-[11px] leading-relaxed ${bulkResult.ok ? "text-[#10B981]" : "text-[#EF4444]"}`} data-testid={`result-${program}`}>
              {bulkResult.ok ? "✓ " : "✗ "}{bulkResult.msg}
            </div>
          )}
        </div>
      </details>
    </div>
  );
}
